import { ActionIcon, Group, LoadingOverlay, Pagination } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconEdit, IconTrash } from '@tabler/icons-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { useChaptersList } from '../../api/hooks/Chapters/useChaptersList'
import { useChapterStore } from '../../store/useChapterStore'
import styles from '../../styles/admin/ChapterList.module.scss'
import styles2 from '../../styles/admin/Main.module.scss'
import { ChapterItem } from '../../types/chapters'
import SaveButton from '../common/SaveButton'
import Table from '../common/Table'
import ChapterAddModal from './ChapterAddModal'
import ChapterDeleteModal from './ChapterDeleteModal'
import ChapterEditModal from './ChapterEditModal'
import Dropdown from '../common/Select'
import { notifications } from '@mantine/notifications'
import { Icon } from '../../assets/icons'

const ChapterList: React.FC = () => {
  const { dataList, setDataList, clearError } = useChapterStore()
  // TODO: 추후 특정 레벨 조회시
  const [selectedLevel, setSelectedLevel] = useState<string>('A')
  const shouldResetPage = useRef(true)

  const { data: chapterListData, isLoading } = useChaptersList({
    LEVEL: selectedLevel,
  })

  const [addModalOpened, { open: openAddModal, close: closeAddModal }] =
    useDisclosure(false)
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false)
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false)

  const [selectedItem, setSelectedItem] = useState<ChapterItem | null>(null)

  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)

  // 페이지네이션 로직
  const { paginatedData, totalPages } = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const paginatedItems = dataList.slice(startIndex, endIndex)
    const totalPagesCount = Math.ceil(dataList.length / itemsPerPage)

    return {
      paginatedData: paginatedItems,
      totalPages: Math.max(totalPagesCount, 1),
    }
  }, [dataList, currentPage, itemsPerPage])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  useEffect(() => {
    if (!chapterListData) return

    if (chapterListData.success && chapterListData.data) {
      setDataList(chapterListData.data)
      clearError()
      if (shouldResetPage.current) {
        setCurrentPage(1)
      }
      shouldResetPage.current = true
    }

    if (!chapterListData.success && chapterListData.message) {
      notifications.show({
        title: 'Error',
        message: chapterListData.message,
        color: 'red',
      })
    }
  }, [chapterListData, setDataList, clearError])

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const handleEditModalClick = (item: ChapterItem) => {
    setSelectedItem(item)
    openEditModal()
  }

  const handleDeleteModalClick = (item: ChapterItem) => {
    setSelectedItem(item)
    openDeleteModal()
  }

  const handleCloseEditModal = () => {
    setSelectedItem(null)
    closeEditModal()
    shouldResetPage.current = false
  }

  const handleCloseDeleteModal = () => {
    setSelectedItem(null)
    closeDeleteModal()
    shouldResetPage.current = false
  }

  return (
    <div className={styles.container} style={{ position: 'relative' }}>
      <h3 style={{ marginBottom: '30px' }}>Chapters</h3>

      <LoadingOverlay visible={isLoading} />
      <div className={styles2.headerSection}>
        <div className={styles2.leftSection}>
          <p>Class List</p>
          <Dropdown value={selectedLevel} onChange={setSelectedLevel} />
        </div>

        <div className={styles.buttonContainer}>
          <SaveButton
            onClick={openAddModal}
            className={styles.addButton}
            disabled={isLoading}
          >
            Add Chapter
          </SaveButton>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <Table>
          <thead>
            <tr>
              <Table.Header>Level</Table.Header>
              <Table.Header>Depth2</Table.Header>
              <Table.Header>Depth3</Table.Header>
              <Table.Header>Depth4</Table.Header>
              <Table.Header>Depth5</Table.Header>
              <Table.Header>Main</Table.Header>
              <Table.Header>Sub</Table.Header>
              <Table.Header>Unit</Table.Header>
              <Table.Header>Detail Unit</Table.Header>
              <Table.Header>Action</Table.Header>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((item) => (
                <tr key={item.LEVEL_ID}>
                  <Table.Cell>{item.LEVEL}</Table.Cell>
                  <Table.Cell>{item.DEPTH2 || '-'}</Table.Cell>
                  <Table.Cell>{item.DEPTH3 || '-'}</Table.Cell>
                  <Table.Cell>{item.DEPTH4 || '-'}</Table.Cell>
                  <Table.Cell>{item.DEPTH5 || '-'}</Table.Cell>
                  <Table.Cell>{item.MAIN || '-'}</Table.Cell>
                  <Table.Cell>{item.SUB || '-'}</Table.Cell>
                  <Table.Cell>{item.UNIT || '-'}</Table.Cell>
                  <Table.Cell>{item.DETAIL_UNIT || '-'}</Table.Cell>
                  <Table.Cell>
                    <Group
                      gap="xs"
                      className={styles.actionWrapper}
                      justify="center"
                      align="center"
                    >
                      <Icon
                        name="pen"
                        size={16}
                        onClick={() => handleEditModalClick(item)}
                        color={'#b6bfca'}
                      />

                      <Icon
                        name="trash"
                        size={20}
                        onClick={() => handleDeleteModalClick(item)}
                        color={'#b6bfca'}
                      />
                    </Group>
                  </Table.Cell>
                </tr>
              ))
            ) : (
              <tr>
                <Table.Cell colSpan={10}>
                  <Table.Empty>
                    {isLoading ? 'Loading...' : 'No data available.'}
                  </Table.Empty>
                </Table.Cell>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {dataList.length > 0 && (
        <div
          className={styles.paginationWrapper}
          style={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          <Pagination
            value={currentPage}
            onChange={handlePageChange}
            total={totalPages}
            size="sm"
            withEdges={false}
            color="gray"
            classNames={{
              control: styles.paginationControl,
            }}
          />
        </div>
      )}

      <ChapterAddModal opened={addModalOpened} onClose={closeAddModal} />

      <ChapterEditModal
        opened={editModalOpened}
        onClose={handleCloseEditModal}
        item={selectedItem}
      />

      <ChapterDeleteModal
        opened={deleteModalOpened}
        onClose={handleCloseDeleteModal}
        item={selectedItem}
      />
    </div>
  )
}

export default ChapterList
